import { useEffect } from 'react';
import s from './BottomSheet.module.css';
import type { ITask, TTaskCategory } from '../data/types';
import { roadmap } from '../data/roadmap';

interface IBottomSheetProps {
  task: ITask | null;
  onClose: () => void;
}

const STATUS_LABEL: Record<ITask['status'], string> = {
  done: 'Готово',
  in_progress: 'В работе',
  todo: 'В планах',
};

const CATEGORY_LABEL: Record<TTaskCategory, string> = {
  backend: 'Сервер',
  'frontend-partner': 'Кабинет врача',
  'frontend-client': 'Кабинет пациента',
  'frontend-admin': 'Управление',
  database: 'Данные',
  infra: 'Платформа',
  integration: 'Внешние сервисы',
};

const Close = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
    <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
  </svg>
);

export const BottomSheet = ({ task, onClose }: IBottomSheetProps) => {
  useEffect(() => {
    if (!task) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [task, onClose]);

  if (!task) return null;

  const allTasks = roadmap.phases.flatMap((p) => p.tasks);
  const taskById = new Map(allTasks.map((t) => [t.id, t]));
  const dependencies = (task.dependsOn ?? [])
    .map((id) => taskById.get(id))
    .filter((t): t is ITask => Boolean(t));

  const statusClass =
    task.status === 'done' ? 'done' : task.status === 'in_progress' ? 'inProgress' : 'todo';

  return (
    <>
      <div className={s.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={s.sheet} role="dialog" aria-modal="true">
        <div className={s.handle} aria-hidden="true" />
        <div className={s.head}>
          <div className={s.headLeft}>
            <span className={s.headTitle}>{task.title}</span>
            <div className={s.headRow}>
              <span className={`${s.statusPill} ${s[statusClass]}`}>
                <span className={s.statusDot} />
                {STATUS_LABEL[task.status]}
              </span>
              <span className={s.categoryPill}>{CATEGORY_LABEL[task.category]}</span>
            </div>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Закрыть">
            <Close />
          </button>
        </div>
        <div className={s.body}>
          <p className={s.description}>{task.description}</p>

          {dependencies.length > 0 && (
            <div className={s.section}>
              <span className={s.sectionLabel}>Сначала нужно</span>
              <div className={s.deps}>
                {dependencies.map((d) => (
                  <div key={d.id} className={s.depItem}>
                    <span
                      className={s.statusDot}
                      style={{
                        background:
                          d.status === 'done'
                            ? 'var(--done)'
                            : d.status === 'in_progress'
                              ? 'var(--progress)'
                              : 'var(--text-quiet)',
                      }}
                    />
                    {d.title}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
